import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import VotingList from '../components/VotingList';
import QuestionBankContent from '../components/QuestionBankContent';
import VotingViewModeDropdown from '../components/VotingViewModeDropdown';
import { UPDATE_ONLINE_STATUS } from '../graphql/user';
import { useAuth } from '../contexts/AuthContext';

const Voting = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const tabParam = searchParams.get('tab');
  // 如果未登入且嘗試訪問擇友標準，強制使用投票廣場
  const initialTab = (!user && tabParam === 'questions') ? 'voting' : (tabParam === 'questions' ? 'questions' : 'voting');
  const [activeTab, setActiveTab] = useState(initialTab); // 'voting' or 'questions'
  const [viewMode, setViewMode] = useState('all'); // 'all' | 'myPosts'

  // 更新上線狀態
  const [updateOnlineStatus] = useMutation(UPDATE_ONLINE_STATUS);

  // 更新上線狀態
  useEffect(() => {
    if (user && user.id) {
      updateOnlineStatus();
    }
  }, [user, updateOnlineStatus]);

  // 如果未登入且當前在擇友標準標籤，自動切換到投票廣場
  useEffect(() => {
    if (!user && activeTab === 'questions') {
      setActiveTab('voting');
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('tab');
        return next;
      });
    }
  }, [user, activeTab, setSearchParams]);

  // 當 URL 參數改變時同步狀態
  useEffect(() => {
    // 如果未登入且嘗試訪問擇友標準，強制切換到投票廣場
    if (!user && tabParam === 'questions') {
      setActiveTab('voting');
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('tab');
        return next;
      });
    } else if (tabParam === 'questions' && activeTab !== 'questions' && user) {
      setActiveTab('questions');
      setViewMode('all');
    } else if (tabParam !== 'questions' && activeTab !== 'voting') {
      setActiveTab('voting');
    }
  }, [tabParam, user, activeTab, setSearchParams]); // 移除 activeTab 依賴，避免循環

  // 當切換到其他tab時，更新 URL 並重置 viewMode
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab !== 'voting') {
      setViewMode('all');
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'voting') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 頁面標題 */}
        <div className="mb-8">
          <div className="flex items-center justify-between gap-4 mb-2">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-600 to-purple-600 bg-clip-text text-transparent">投票＆評分</h1>
            <button 
              onClick={() => navigate('/voting/create')}
              className="px-4 sm:px-6 py-2 bg-gradient-to-r from-pink-400 to-purple-500 text-white rounded-lg hover:from-pink-500 hover:to-purple-600 transition-colors whitespace-nowrap text-sm sm:text-base flex-shrink-0 font-medium"
            >
              ➕ 創建投票
            </button>
          </div>
          <p className="text-sm sm:text-base text-gray-600">投票彰顯個人特色，給選項評分加入擇友標準</p>
        </div>

        {/* Tab Selector */}
        <div className="mb-6">
          <div className="flex w-full border-b-2 border-gray-200">
            <div className={user ? "flex-1" : "flex-1"}>
              {activeTab === 'voting' ? (
                <VotingViewModeDropdown
                  viewMode={viewMode}
                  onChange={setViewMode}
                  isAuthenticated={!!user}
                  isSemiAdmin={user?.isSemiAdmin || false}
                  isTabStyle={true}
                  fullWidth={true}
                />
              ) : (
                <button
                  onClick={() => handleTabChange('voting')}
                  className="w-full px-4 py-3 text-base font-bold transition-all whitespace-nowrap text-center text-gray-400 hover:text-gray-600"
                >
                  投票廣場
                </button>
              )}
            </div>
            {user && (
              <div className="flex-1">
                <button
                  onClick={() => handleTabChange('questions')}
                  className={`w-full px-4 py-3 text-base font-bold transition-all whitespace-nowrap text-center ${
                    activeTab === 'questions'
                      ? 'text-purple-600 border-b-4 border-purple-600 -mb-0.5'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  擇友標準
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'voting' ? (
            <VotingList navigate={navigate} viewMode={viewMode} onViewModeChange={setViewMode} />
          ) : (
            <QuestionBankContent />
          )}
        </div>
      </div>
    </div>
  );
};

export default Voting; 